<template>
  <div class="dev-container">

    <!-- Header -->
    <div class="dev-header">
      <div>
        <h2 class="dev-title">Dispositivos de Acceso</h2>
        <p class="dev-subtitle">Gestión de biométricos, torniquetes y lectores registrados</p>
      </div>
      <button class="btn-agregar" @click="abrirFormNuevo">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-sm">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Nuevo dispositivo
      </button>
    </div>

    <!-- Tabla -->
    <div class="tabla-wrapper">
      <div v-if="cargando" class="estado-vacio">
        <v-progress-circular indeterminate color="primary" size="40"/>
        <p>Cargando dispositivos...</p>
      </div>

      <div v-else-if="dispositivos.length === 0" class="estado-vacio">
        <p>No hay dispositivos registrados.</p>
      </div>

      <table v-else class="dev-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>IP / Puerto</th>
            <th>Estado</th>
            <th>Última conexión</th>
            <th class="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in dispositivos" :key="d.id">
            <td class="td-nombre">{{ d.name }}</td>
            <td>
              <span class="badge-tipo">{{ d.deviceType || '—' }}</span>
            </td>
            <td class="td-ip">{{ d.ip }}<span class="puerto">:{{ d.port }}</span></td>
            <td>
              <span :class="['badge-status', d.status === 'ONLINE' ? 'online' : 'offline']">
                <span class="dot"></span>{{ d.status }}
              </span>
            </td>
            <td class="td-fecha">{{ formatFecha(d.lastConnection) }}</td>
            <td class="col-actions">
              <button class="btn-icon btn-test" @click="probarConexion(d)" :disabled="d.probando" title="Probar conexión">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-xs">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.07 3.38 2 2 0 0 1 3.07 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/>
                </svg>
              </button>
              <button class="btn-icon btn-delete" @click="confirmarEliminar(d)" title="Eliminar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-xs">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Dialog: Nuevo dispositivo -->
    <div v-if="dialogForm" class="dialog-overlay" @click.self="cerrarForm">
      <div class="dialog-box">
        <h3 class="dialog-title">Nuevo dispositivo</h3>

        <div class="form-grid">
          <label class="form-label" style="grid-column: 1 / -1">
            Nombre *
            <input v-model="form.name" class="form-input" placeholder="Ej: Puerta Principal" />
          </label>
          <label class="form-label">
            Tipo
            <select v-model="form.deviceType" class="form-input">
              <option value="">— Seleccionar —</option>
              <option value="BIOMETRICO">Biométrico</option>
              <option value="RFID">RFID</option>
              <option value="FACIAL">Facial</option>
              <option value="TORNIQUETE">Torniquete</option>
              <option value="contrl_acceso">Control de acceso</option>
            </select>
          </label>
          <label class="form-label">
            IP *
            <input v-model="form.ip" class="form-input" placeholder="192.168.10.xxx" />
          </label>
          <label class="form-label">
            Puerto *
            <input v-model.number="form.port" class="form-input" type="number" placeholder="80" />
          </label>
          <label class="form-label">
            Usuario *
            <input v-model="form.username" class="form-input" placeholder="admin" />
          </label>
          <label class="form-label">
            Contraseña *
            <input v-model="form.password" class="form-input" type="password" placeholder="••••••••" />
          </label>
        </div>

        <p v-if="errorForm" class="form-error">{{ errorForm }}</p>

        <div class="dialog-actions">
          <button class="btn-cancelar" @click="cerrarForm" :disabled="guardando">Cancelar</button>
          <button class="btn-guardar" @click="guardar" :disabled="guardando">
            {{ guardando ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Dialog: Confirmar eliminar -->
    <div v-if="dialogEliminar" class="dialog-overlay" @click.self="dialogEliminar = false">
      <div class="dialog-box dialog-sm">
        <h3 class="dialog-title">Eliminar dispositivo</h3>
        <p class="dialog-text">
          ¿Eliminar <strong>{{ dispositivoAEliminar && dispositivoAEliminar.name }}</strong>?
          Se perderá la configuración y los usuarios sincronizados en este dispositivo.
        </p>
        <p v-if="errorEliminar" class="form-error">{{ errorEliminar }}</p>
        <div class="dialog-actions">
          <button class="btn-cancelar" @click="dialogEliminar = false" :disabled="eliminando">Cancelar</button>
          <button class="btn-eliminar" @click="eliminar" :disabled="eliminando">
            {{ eliminando ? 'Eliminando...' : 'Eliminar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Snackbar resultado -->
    <div v-if="snack.visible" :class="['snack', snack.tipo]">{{ snack.msg }}</div>

  </div>
</template>

<script>
import ApiService from '../../Servicios/ApiService'

const DB_NAME = process.env.VUE_APP_CENTINEL_DB || 'security_db'

export default {
  name: 'Dispositivos',

  data: () => ({
    dispositivos:         [],
    cargando:             false,
    dialogForm:           false,
    guardando:            false,
    errorForm:            null,
    dialogEliminar:       false,
    eliminando:           false,
    errorEliminar:        null,
    dispositivoAEliminar: null,
    form: { name: '', ip: '', port: 80, username: 'admin', password: '', deviceType: '', enabled: true },
    snack: { visible: false, msg: '', tipo: 'ok', _t: null }
  }),

  async mounted () {
    await this.cargar()
  },

  methods: {
    async cargar () {
      this.cargando = true
      try {
        const res        = await ApiService.centinel.get('/api/devices')
        const raw        = (res && res.data) ? res.data : (Array.isArray(res) ? res : [])
        this.dispositivos = raw.map(d => ({ ...d, probando: false }))
      } catch (e) {
        this.mostrarSnack('Error al cargar dispositivos', 'error')
      } finally {
        this.cargando = false
      }
    },

    abrirFormNuevo () {
      this.form     = { name: '', ip: '', port: 80, username: 'admin', password: '', deviceType: '', enabled: true }
      this.errorForm = null
      this.dialogForm = true
    },

    cerrarForm () {
      this.dialogForm = false
      this.errorForm  = null
    },

    async guardar () {
      if (!this.form.name || !this.form.ip || !this.form.port || !this.form.username || !this.form.password) {
        this.errorForm = 'Completa los campos obligatorios (*).'; return
      }
      this.guardando = true
      this.errorForm = null
      try {
        await ApiService.centinel.post('/api/devices', { ...this.form, databaseName: DB_NAME })
        await this.cargar()
        this.cerrarForm()
        this.mostrarSnack('Dispositivo creado correctamente', 'ok')
      } catch (e) {
        this.errorForm = (e.response && e.response.data && e.response.data.message)
          ? e.response.data.message : 'Error al guardar.'
      } finally {
        this.guardando = false
      }
    },

    confirmarEliminar (d) {
      this.dispositivoAEliminar = d
      this.errorEliminar        = null
      this.dialogEliminar       = true
    },

    async eliminar () {
      this.eliminando    = true
      this.errorEliminar = null
      try {
        await ApiService.centinel.delete(`/api/devices/${this.dispositivoAEliminar.id}`)
        this.dialogEliminar = false
        await this.cargar()
        this.mostrarSnack('Dispositivo eliminado', 'ok')
      } catch (e) {
        this.errorEliminar = (e.response && e.response.data && e.response.data.message)
          ? e.response.data.message : 'Error al eliminar.'
      } finally {
        this.eliminando = false
      }
    },

    async probarConexion (d) {
      this.$set(d, 'probando', true)
      try {
        await ApiService.centinel.get(`/api/devices/test/${d.id}`)
        d.status = 'ONLINE'
        this.mostrarSnack(`${d.name}: conexión exitosa`, 'ok')
      } catch (e) {
        d.status = 'OFFLINE'
        this.mostrarSnack(`${d.name}: sin respuesta`, 'error')
      } finally {
        this.$set(d, 'probando', false)
      }
    },

    formatFecha (iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      return d.toLocaleDateString('es-EC') + ' ' + d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
    },

    mostrarSnack (msg, tipo) {
      clearTimeout(this.snack._t)
      this.snack = { visible: true, msg, tipo, _t: setTimeout(() => { this.snack.visible = false }, 3500) }
    }
  }
}
</script>

<style scoped>
.dev-container {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
  font-family: var(--font-family);
}

.dev-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.dev-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 4px;
}

.dev-subtitle { color: var(--color-text-secondary); font-size: 0.9rem; margin: 0; }

.btn-agregar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: var(--color-primary);
  color: var(--color-bg);
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: var(--shadow-glow);
  transition: all 0.2s;
}
.btn-agregar:hover { transform: translateY(-1px); }

/* Tabla */
.tabla-wrapper {
  background: #272f27;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
}

.dev-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.dev-table th {
  background: var(--color-surface-2);
  color: var(--color-text-secondary);
  font-weight: 600;
  padding: 12px 14px;
  text-align: left;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dev-table td {
  padding: 13px 14px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-primary);
}

.dev-table tbody tr:last-child td { border-bottom: none; }
.dev-table tbody tr:hover { background: var(--color-surface-2); }

.td-nombre { font-weight: 600; }
.td-ip { font-family: monospace; font-size: 0.88rem; color: var(--color-text-secondary); }
.puerto { opacity: 0.6; }
.td-fecha { font-size: 0.82rem; color: var(--color-text-muted); }

.badge-tipo {
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  padding: 2px 9px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-status.online  { background: var(--color-primary-dim); color: var(--color-primary); border: 1px solid rgba(19,236,19,0.3); }
.badge-status.offline { background: rgba(239,68,68,0.1); color: var(--color-error); border: 1px solid rgba(239,68,68,0.3); }

.dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }

.col-actions { width: 90px; text-align: center; }

.btn-icon {
  padding: 6px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s;
  margin: 0 2px;
}

.btn-test  { background: rgba(19,236,19,0.1); color: var(--color-primary); }
.btn-test:hover:not(:disabled) { background: rgba(19,236,19,0.25); }
.btn-test:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-delete { background: rgba(239,68,68,0.1); color: var(--color-error); }
.btn-delete:hover { background: rgba(239,68,68,0.25); }

/* Dialog */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.dialog-box {
  background: #1c2e1c;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  padding: 28px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
}

.dialog-sm { max-width: 400px; }

.dialog-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 20px;
}

.dialog-text { color: var(--color-text-secondary); font-size: 0.95rem; margin: 0 0 4px; }

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.82rem;
  color: var(--color-text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.form-input {
  padding: 8px 10px;
  border-radius: 7px;
  border: 1px solid var(--color-border);
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  outline: none;
}
.form-input:focus { border-color: var(--color-primary); }

.form-error {
  color: var(--color-error);
  font-size: 0.85rem;
  margin: 12px 0 0;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
}

.btn-cancelar {
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-guardar {
  padding: 8px 22px;
  border-radius: 8px;
  border: none;
  background: var(--color-primary);
  color: var(--color-bg);
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
}
.btn-guardar:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-eliminar {
  padding: 8px 22px;
  border-radius: 8px;
  border: none;
  background: var(--color-error);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
}
.btn-eliminar:disabled { opacity: 0.5; cursor: not-allowed; }

/* Snackbar */
.snack {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
}
.snack.ok    { background: var(--color-primary); color: var(--color-bg); }
.snack.error { background: var(--color-error);   color: #fff; }

.estado-vacio {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
  color: var(--color-text-muted);
}

.icon-sm { width: 16px; height: 16px; }
.icon-xs { width: 15px; height: 15px; }

@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
  .dev-table { font-size: 0.8rem; }
}
</style>



// WEBPACK FOOTER //
// src/components/Configuracion/Dispositivos.vue